import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { BigButton, Card, FIELD, FieldLabel, FilterChip, HeaderActions, KpiCard, Modal, Segmented, StatusPill, Tone } from '../../components/ui/dg';
import { getFees, PlatformFees, FeeRule } from '../../api/services/feesService';
import { getAllUsersWithToken } from '../../api/services/usersService';
import {
  createOwnerPromo, getOwnerPromos, getOwnerPromoUses, updateOwnerPromo,
  OwnerPromo, OwnerPromoInput, OwnerPromoList, OwnerPromoUse, PromoDiscountType, PromoEligibility, PromoFeeCode, PromoState
} from '../../api/services/ownerPromosService';

/**
 * Section Codes promo (administrateurs) : des codes pour les PROPRIÉTAIRES, qui
 * réduisent ce qu'ils reversent à Dingo sur les frais choisis. Le locataire ne
 * voit rien et paie toujours la même chose. La remise est figée sur chaque
 * location (API, migration 094) : modifier un code ne change pas le passé.
 */

const FEES: { code: PromoFeeCode; label: string; hint: string }[] = [
  { code: 'owner_commission', label: 'Commission Dingo', hint: 'Prise sur le loyer' },
  { code: 'renter_service', label: 'Frais de service', hint: 'Gardés par le propriétaire au lieu d’être reversés' },
  { code: 'renter_protection', label: 'Frais de protection', hint: 'Gardés par le propriétaire au lieu d’être reversés' },
];
const FEE_SHORT: Record<PromoFeeCode, string> = {
  owner_commission: 'Commission', renter_service: 'Service', renter_protection: 'Protection'
};
const STATE: Record<PromoState, { label: string; tone: Tone }> = {
  active: { label: 'Actif', tone: 'green' },
  scheduled: { label: 'Programmé', tone: 'indigo' },
  full: { label: 'Complet', tone: 'amber' },
  ended: { label: 'Terminé', tone: 'grey' },
  disabled: { label: 'Désactivé', tone: 'grey' },
};
const FILTERS: { id: 'live' | 'scheduled' | 'over' | 'all'; label: string; states: PromoState[] }[] = [
  { id: 'live', label: 'Actifs', states: ['active'] },
  { id: 'scheduled', label: 'Programmés', states: ['scheduled'] },
  { id: 'over', label: 'Terminés', states: ['ended', 'full', 'disabled'] },
  { id: 'all', label: 'Tous', states: ['active', 'scheduled', 'ended', 'full', 'disabled'] },
];

const dt = (value: number) => `${(Math.round(value * 1000) / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 3 })} DT`;
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const feeFor = (rule: FeeRule | undefined, price: number) => {
  if (!rule || rule.percent <= 0) return rule?.min_amount || 0;
  return Math.max((price * rule.percent) / 100, rule.min_amount);
};
const ruleText = (rule?: FeeRule) => {
  if (!rule || (rule.percent <= 0 && rule.min_amount <= 0)) return 'Offert aujourd’hui';
  if (rule.percent <= 0) return dt(rule.min_amount);
  return rule.min_amount > 0 ? `${rule.percent} %, au moins ${dt(rule.min_amount)}` : `${rule.percent} %`;
};

const discountText = (promo: Pick<OwnerPromo, 'discount_type' | 'discount_value'>) =>
  promo.discount_type === 'free' ? 'Offerts'
    : promo.discount_type === 'fixed' ? `−${dt(promo.discount_value)} par location`
      : `−${promo.discount_value} %`;
const limitText = (promo: OwnerPromo) => [
  promo.per_owner_rentals ? `${promo.per_owner_rentals} location${promo.per_owner_rentals > 1 ? 's' : ''} chacun` : null,
  promo.eligibility === 'new' ? 'nouveaux propriétaires' : promo.eligibility === 'selected' ? `${promo.allowed_owner_ids.length} propriétaire${promo.allowed_owner_ids.length > 1 ? 's' : ''} choisi${promo.allowed_owner_ids.length > 1 ? 's' : ''}` : null,
].filter(Boolean).join(', ');

const OwnerPromos: React.FC = () => {
  const { token } = useAuth();
  const [data, setData] = useState<OwnerPromoList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<typeof FILTERS[number]['id']>('live');
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [fees, setFees] = useState<PlatformFees | null>(null);

  const load = useCallback(() => {
    if (!token) return;
    getOwnerPromos(token).then((d) => { setData(d); setError(null); }).catch((e) => setError(e.message));
  }, [token]);
  useEffect(load, [load]);
  useEffect(() => { if (token) getFees(token).then(setFees).catch(() => {}); }, [token]);

  const codes = useMemo(() => data?.codes || [], [data]);
  const visible = useMemo(() => {
    const states = FILTERS.find((f) => f.id === filter)!.states;
    return codes.filter((c) => states.includes(c.state));
  }, [codes, filter]);
  const opened = codes.find((c) => c.id_owner_promo === openId) || null;

  return (
    <main className="flex-1 overflow-y-auto bg-[#F7F7FB] px-4 pb-8 pt-1 lg:px-7">
      <HeaderActions>
        <button type="button" onClick={() => setCreating(true)}
          className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-[#4F46E5] px-3.5 text-[13px] font-bold text-white hover:bg-[#4338CA]">
          Nouveau code
        </button>
      </HeaderActions>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiCard label="Codes actifs" value={codes.filter((c) => c.state === 'active').length} />
        <KpiCard label="Propriétaires avec un code" value={data?.kpis.owners ?? 0} />
        <KpiCard label="Remise accordée ce mois" value={dt(data?.kpis.discount_this_month ?? 0)}
          sub={`${data?.kpis.rentals_this_month ?? 0} location${(data?.kpis.rentals_this_month ?? 0) > 1 ? 's' : ''} terminée${(data?.kpis.rentals_this_month ?? 0) > 1 ? 's' : ''}`} />
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <FilterChip key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)}
            count={codes.filter((c) => f.states.includes(c.state)).length}>
            {f.label}
          </FilterChip>
        ))}
      </div>

      <Card className="mt-3 overflow-hidden">
        {error ? (
          <p className="px-5 py-8 text-center text-[13px] text-[#B91C1C]">{error}</p>
        ) : !data ? (
          <p className="px-5 py-8 text-center text-[13px] text-[#6B7280]">Chargement…</p>
        ) : visible.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <p className="text-[15px] font-bold text-[#1E293B]">{codes.length === 0 ? 'Aucun code pour le moment' : 'Aucun code ici'}</p>
            <p className="mt-1 text-[13px] text-[#6B7280]">Un code réduit ce que les propriétaires reversent à Dingo. Les locataires n’en voient rien.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-[13px]">
              <thead>
                <tr className="text-left text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]">
                  <th className="px-5 py-3">Code</th><th className="px-3 py-3">Sur</th><th className="px-3 py-3">Réduction</th>
                  <th className="px-3 py-3">Propriétaires</th><th className="px-3 py-3">Remise accordée</th><th className="px-3 py-3">Fin</th><th className="px-5 py-3">État</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => (
                  <tr key={c.id_owner_promo} onClick={() => setOpenId(c.id_owner_promo)}
                    className="cursor-pointer border-t border-[#EEF0F4] hover:bg-[#FBFBFE]">
                    <td className="px-5 py-3">
                      <span className="font-extrabold tracking-wide text-[#1E293B]">{c.code}</span>
                      {c.description && <span className="block truncate text-[12px] text-[#6B7280]">{c.description}</span>}
                    </td>
                    <td className="px-3 py-3">
                      <span className="flex flex-wrap gap-1">{c.fee_codes.map((f) => <StatusPill key={f} tone="indigo">{FEE_SHORT[f]}</StatusPill>)}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-semibold text-[#1E293B]">{discountText(c)}</span>
                      {limitText(c) && <span className="block text-[12px] text-[#6B7280]">{limitText(c)}</span>}
                    </td>
                    <td className="px-3 py-3 tabular-nums">
                      {c.owners_count}{c.max_owners ? ` / ${c.max_owners}` : ''}
                      {c.max_owners ? (
                        <span className="mt-1 block h-1 w-16 overflow-hidden rounded-full bg-[#EEF0F4]">
                          <span className="block h-full bg-[#4F46E5]" style={{ width: `${Math.min(100, (c.owners_count / c.max_owners) * 100)}%` }} />
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 font-semibold tabular-nums text-[#1E293B]">{dt(c.discount_granted)}</td>
                    <td className="px-3 py-3 text-[#6B7280]">{c.ends_at ? day(c.ends_at) : 'Sans fin'}</td>
                    <td className="px-5 py-3"><StatusPill tone={STATE[c.state].tone}>{STATE[c.state].label}</StatusPill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && token && (
        <PromoEditor token={token} fees={fees} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}
      {opened && token && (
        <PromoDetail token={token} promo={opened} onClose={() => setOpenId(null)} onChanged={load} />
      )}
    </main>
  );
};

/** Création d'un code : frais visés, réduction, limites, public ; l'effet sur un exemple. */
const PromoEditor: React.FC<{ token: string; fees: PlatformFees | null; onClose: () => void; onSaved: () => void }> = ({ token, fees, onClose, onSaved }) => {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [feeCodes, setFeeCodes] = useState<PromoFeeCode[]>(['owner_commission']);
  const [type, setType] = useState<PromoDiscountType>('percentage');
  const [value, setValue] = useState('50');
  const [perOwner, setPerOwner] = useState('');
  const [maxOwners, setMaxOwners] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [eligibility, setEligibility] = useState<PromoEligibility>('all');
  const [owners, setOwners] = useState<{ id: string; name: string; email?: string }[]>([]);
  const [chosen, setChosen] = useState<string[]>([]);
  const [ownerQuery, setOwnerQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (eligibility !== 'selected' || owners.length) return;
    getAllUsersWithToken(token).then((users: any[]) => setOwners((Array.isArray(users) ? users : []).map((u) => ({
      id: u.id_user, name: [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || 'Compte sans nom', email: u.email
    })).sort((a, b) => a.name.localeCompare(b.name, 'fr')))).catch(() => {});
  }, [eligibility, owners.length, token]);

  const toggleFee = (f: PromoFeeCode) => setFeeCodes((cur) => cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]);

  // Exemple sur une location de 100 DT, aux taux d'aujourd'hui.
  const example = useMemo(() => {
    if (!fees) return null;
    const price = 100;
    const service = feeFor(fees.renter_service, price);
    const protection = feeFor(fees.renter_protection, price);
    const commission = feeFor(fees.owner_commission, price);
    const share = fees.owner_fee_share?.percent ? ((service + protection) * fees.owner_fee_share.percent) / 100 : 0;
    const kept = (fee: number) => (service + protection > 0 ? (share * fee) / (service + protection) : 0);
    const parts: Record<PromoFeeCode, number> = {
      owner_commission: commission, renter_service: service - kept(service), renter_protection: protection - kept(protection)
    };
    const baseAmount = feeCodes.reduce((sum, f) => sum + parts[f], 0);
    const n = Number(value.replace(',', '.')) || 0;
    const discount = type === 'free' ? baseAmount : type === 'percentage' ? (baseAmount * Math.min(n, 100)) / 100 : Math.min(n, baseAmount);
    const owed = service + protection + commission - share;
    return { owed, discount, after: owed - discount };
  }, [fees, feeCodes, type, value]);

  const save = async () => {
    setSaving(true);
    setError(null);
    const input: OwnerPromoInput = {
      code: code.trim().toUpperCase(),
      description: description.trim() || null,
      fee_codes: feeCodes,
      discount_type: type,
      discount_value: type === 'free' ? 0 : Number(value.replace(',', '.')),
      per_owner_rentals: perOwner ? Number(perOwner) : null,
      max_owners: maxOwners ? Number(maxOwners) : null,
      starts_at: startsAt ? new Date(`${startsAt}T00:00:00`).toISOString() : null,
      ends_at: endsAt ? new Date(`${endsAt}T23:59:59`).toISOString() : null,
      eligibility,
      allowed_owner_ids: eligibility === 'selected' ? chosen : [],
    };
    try {
      await createOwnerPromo(token, input);
      onSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const suggestions = ownerQuery.trim()
    ? owners.filter((o) => !chosen.includes(o.id) && `${o.name} ${o.email || ''}`.toLowerCase().includes(ownerQuery.trim().toLowerCase())).slice(0, 5)
    : [];

  return (
    <Modal title="Nouveau code" subtitle="Pour les propriétaires. Le code réduit ce qu’ils reversent à Dingo ; leurs locataires paient toujours la même chose." onClose={onClose} width={620}>
      <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
        <div>
          <FieldLabel htmlFor="promo-code">Code</FieldLabel>
          <input id="promo-code" className={`${FIELD} uppercase tracking-wide`} value={code} maxLength={40} placeholder="LANCEMENT26"
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))} />
        </div>
        <div>
          <FieldLabel htmlFor="promo-desc">Description interne</FieldLabel>
          <input id="promo-desc" className={FIELD} value={description} maxLength={300} placeholder="Lancement Tunis, novembre" onChange={(e) => setDescription(e.target.value)} />
        </div>
      </div>

      <FieldLabel>S’applique à</FieldLabel>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {FEES.map((f) => {
          const on = feeCodes.includes(f.code);
          return (
            <button key={f.code} type="button" onClick={() => toggleFee(f.code)}
              className={`flex items-start gap-2.5 rounded-2xl border-[1.5px] p-3 text-left transition-colors ${on ? 'border-[#4F46E5] bg-[#F7F6FF]' : 'border-[#E5E7EB] hover:bg-gray-50'}`}>
              <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[5px] border-[1.5px] text-[10px] text-white ${on ? 'border-[#4F46E5] bg-[#4F46E5]' : 'border-[#CBCFDB]'}`}>{on ? '✓' : ''}</span>
              <span>
                <span className="block text-[13px] font-bold text-[#1E293B]">{f.label}</span>
                <span className="block text-[11.5px] leading-[1.35] text-[#6B7280]">{f.hint}, {ruleText(fees?.[f.code])}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
        <div>
          <FieldLabel>Réduction</FieldLabel>
          <Segmented ariaLabel="Type de réduction" value={type} onChange={(id) => setType(id as PromoDiscountType)}
            options={[{ id: 'percentage', label: 'Pourcentage' }, { id: 'fixed', label: 'Montant' }, { id: 'free', label: 'Offerts' }]} />
        </div>
        {type !== 'free' && (
          <div>
            <FieldLabel htmlFor="promo-value">{type === 'percentage' ? 'Pourcentage' : 'Montant par location (DT)'}</FieldLabel>
            <input id="promo-value" className={FIELD} inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
          </div>
        )}
        <div>
          <FieldLabel htmlFor="promo-per">Locations par propriétaire</FieldLabel>
          <input id="promo-per" className={FIELD} inputMode="numeric" placeholder="Sans limite" value={perOwner} onChange={(e) => setPerOwner(e.target.value.replace(/\D/g, ''))} />
        </div>
        <div>
          <FieldLabel htmlFor="promo-max">Propriétaires au total</FieldLabel>
          <input id="promo-max" className={FIELD} inputMode="numeric" placeholder="Sans limite" value={maxOwners} onChange={(e) => setMaxOwners(e.target.value.replace(/\D/g, ''))} />
        </div>
        <div>
          <FieldLabel htmlFor="promo-start">Début</FieldLabel>
          <input id="promo-start" type="date" className={FIELD} value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        </div>
        <div>
          <FieldLabel htmlFor="promo-end">Fin</FieldLabel>
          <input id="promo-end" type="date" className={FIELD} value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </div>
      </div>

      <FieldLabel>Qui peut l’utiliser</FieldLabel>
      <Segmented ariaLabel="Public" value={eligibility} onChange={(id) => setEligibility(id as PromoEligibility)}
        options={[{ id: 'all', label: 'Tous' }, { id: 'new', label: 'Nouveaux propriétaires' }, { id: 'selected', label: 'Propriétaires choisis' }]} />
      {eligibility === 'selected' && (
        <div className="mt-3">
          <input className={FIELD} placeholder="Chercher un propriétaire" value={ownerQuery} onChange={(e) => setOwnerQuery(e.target.value)} />
          {suggestions.length > 0 && (
            <div className="mt-1 overflow-hidden rounded-xl border border-[#E5E7EB]">
              {suggestions.map((o) => (
                <button key={o.id} type="button" onClick={() => { setChosen((c) => [...c, o.id]); setOwnerQuery(''); }}
                  className="block w-full px-3 py-2 text-left text-[13px] hover:bg-gray-50">
                  <span className="font-semibold text-[#1E293B]">{o.name}</span>{o.email && <span className="ml-2 text-[#6B7280]">{o.email}</span>}
                </button>
              ))}
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {chosen.map((id) => (
              <button key={id} type="button" onClick={() => setChosen((c) => c.filter((x) => x !== id))}
                className="rounded-full bg-[#EEEDFD] px-2.5 py-1 text-[12px] font-bold text-[#4F46E5]">
                {owners.find((o) => o.id === id)?.name || 'Propriétaire'} ✕
              </button>
            ))}
          </div>
        </div>
      )}

      {example && feeCodes.length > 0 && (
        <p className="mt-5 rounded-2xl bg-[#F7F7FB] px-4 py-3 text-[13px] leading-[1.5] text-[#475569]">
          Exemple, location de 100 DT aux taux d’aujourd’hui. Le propriétaire reverse <b className="text-[#1E293B]">{dt(example.after)}</b> au lieu
          de {dt(example.owed)}, soit <b className="text-[#1E293B]">{dt(example.discount)}</b> gardés en plus.
        </p>
      )}
      {error && <p className="mt-3 text-[13px] text-[#B91C1C]">{error}</p>}
      <div className="mt-6 flex justify-end gap-2">
        <BigButton kind="secondary" onClick={onClose}>Annuler</BigButton>
        <BigButton onClick={save} disabled={saving || !code.trim() || feeCodes.length === 0 || (eligibility === 'selected' && chosen.length === 0)}>
          {saving ? 'Création…' : 'Créer le code'}
        </BigButton>
      </div>
    </Modal>
  );
};

/** Un code : ce qu'il a fait, propriétaire par propriétaire, et de quoi l'arrêter ou le prolonger. */
const PromoDetail: React.FC<{ token: string; promo: OwnerPromo; onClose: () => void; onChanged: () => void }> = ({ token, promo, onClose, onChanged }) => {
  const [uses, setUses] = useState<OwnerPromoUse[] | null>(null);
  const [endsAt, setEndsAt] = useState(promo.ends_at ? promo.ends_at.slice(0, 10) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getOwnerPromoUses(token, promo.id_owner_promo).then(setUses).catch((e) => setError(e.message));
  }, [token, promo.id_owner_promo]);

  const apply = async (input: OwnerPromoInput) => {
    setBusy(true);
    setError(null);
    try {
      await updateOwnerPromo(token, promo.id_owner_promo, input);
      onChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={promo.code} subtitle={[discountText(promo), `sur ${promo.fee_codes.map((f) => FEE_SHORT[f].toLowerCase()).join(', ')}`, limitText(promo)].filter(Boolean).join(' · ')} onClose={onClose} width={600}>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <StatusPill tone={STATE[promo.state].tone}>{STATE[promo.state].label}</StatusPill>
        <span className="text-[12.5px] text-[#6B7280]">
          {promo.owners_count} propriétaire{promo.owners_count > 1 ? 's' : ''} · {promo.rentals_count} location{promo.rentals_count > 1 ? 's' : ''} remisée{promo.rentals_count > 1 ? 's' : ''} · {dt(promo.discount_granted)} accordés
        </span>
      </div>
      {promo.description && <p className="mt-2 text-[13px] text-[#475569]">{promo.description}</p>}

      <FieldLabel>Propriétaires qui l’ont activé</FieldLabel>
      {!uses ? (
        <p className="text-[13px] text-[#6B7280]">Chargement…</p>
      ) : uses.length === 0 ? (
        <p className="text-[13px] text-[#6B7280]">Personne pour le moment.</p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-[#EEF0F4]">
          {uses.map((u) => (
            <div key={u.owner_id} className="flex items-center gap-3 border-t border-[#EEF0F4] px-4 py-2.5 first:border-t-0">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-bold text-[#1E293B]">{u.name}</span>
                <span className="block text-[12px] text-[#6B7280]">Activé le {day(u.activated_at)} · {u.rentals} location{u.rentals > 1 ? 's' : ''}</span>
              </span>
              <span className="text-[13px] font-semibold tabular-nums text-[#1E293B]">{dt(u.saved)}</span>
            </div>
          ))}
        </div>
      )}

      <FieldLabel htmlFor="promo-detail-end">Fin</FieldLabel>
      <div className="flex gap-2">
        <input id="promo-detail-end" type="date" className={FIELD} value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        <BigButton kind="secondary" disabled={busy}
          onClick={() => apply({ ends_at: endsAt ? new Date(`${endsAt}T23:59:59`).toISOString() : null })}>
          Enregistrer
        </BigButton>
      </div>
      <p className="mt-2 text-[12px] text-[#6B7280]">Les remises déjà posées sur des locations restent acquises, même si le code s’arrête.</p>
      {error && <p className="mt-3 text-[13px] text-[#B91C1C]">{error}</p>}
      <div className="mt-6 flex justify-end">
        {promo.is_active ? (
          <BigButton kind="danger" disabled={busy} onClick={() => apply({ is_active: false })}>Désactiver le code</BigButton>
        ) : (
          <BigButton disabled={busy} onClick={() => apply({ is_active: true })}>Réactiver le code</BigButton>
        )}
      </div>
    </Modal>
  );
};

export default OwnerPromos;
